import { useState } from 'react';
import {
  useHistory,
  matchPath,
  useRouteMatch,
  useLocation,
} from 'react-router-dom';
import { t } from 'ttag';

import {
  dayDiscount,
  newArrivals,
  publicMemberProfile,
  swapCollection,
} from '@urls';
import { matchCatalogPath, matchCollectionPath } from '@utils/common';
import { getPathWithoutLang } from '@utils/i18n';
import { SelectOption } from '@ui/Select';
import { HeaderSearch } from '@components/HeaderSearch';

import { useHeaderSearchQuery } from './gql/graphql.v5.generated';

export interface RootCatalog {
  id: number,
  name: string,
  slug: string,
}

interface Props {
  className?: string,
  rootCatalogs?: RootCatalog[],
  isMobile?: boolean,
}

export const HeaderSearchContainer: React.FC<Props> = ({
  className,
  rootCatalogs,
  isMobile,
}) => {
  const history = useHistory();
  const location = useLocation();

  const publicMemberProfileMatch = useRouteMatch<{ username: string }>(publicMemberProfile);
  const currentCatalogMatch = matchCatalogPath(location.pathname);
  const currentCollectionMatch = matchCollectionPath(location.pathname);

  const currentNamespace = currentCatalogMatch?.params.namespace;
  const currentCatalogSlug = currentCatalogMatch && 'slug' in currentCatalogMatch?.params
    ? currentCatalogMatch?.params.slug
    : undefined;

  const defaultSearchOption = {
    value: '/clothes',
    label: t`Всі категорії`,
  };

  const customSearchOptions = [
    {
      value: newArrivals,
      label: t`Новинки`,
    },
    {
      value: dayDiscount,
      label: t`Знижки`,
    },
    {
      value: swapCollection,
      label: t`SWAP-паті`,
    },
  ]
    .concat(
      currentCollectionMatch ? [
        {
          value: currentCollectionMatch.url,
          label: t`По колекції`,
        },
      ] : [],
    )
    .concat(
      publicMemberProfileMatch ? [
        {
          value: publicMemberProfileMatch.url,
          label: t`У продавця`,
        },
      ] : [],
    );

  const customSearchOption = customSearchOptions.find(
    (option) => history.location.pathname !== '/' && matchPath(option.value, history.location.pathname),
  );

  const [selectedCatalog, setSelectedCatalog] = useState<SelectOption<string>>(
    () => customSearchOption || defaultSearchOption,
  );

  const { data } = useHeaderSearchQuery({
    variables: {
      catalogSlug: currentCatalogSlug || currentNamespace,
    },
    context: {
      v5: true,
    },
    onCompleted(response) {
      if (response.catalog) {
        setSelectedCatalog({
          value: getPathWithoutLang(response.catalog.url),
          label: response.catalog.name,
        });
      }
    },
  });

  const handleSelectCatalog = (catalog: SelectOption<string>) => {
    setSelectedCatalog(catalog);
  };

  let catalogsOptions: SelectOption<string>[] = rootCatalogs?.map((catalog) => ({
      value: `/${catalog.slug}`,
      label: catalog.name,
    })) || [];

  if (data?.catalog) {
    catalogsOptions = [
      {
        value: getPathWithoutLang(data.catalog.url) || '',
        label: data.catalog.name,
      },
    ].concat([
      defaultSearchOption,
      ...catalogsOptions,
    ]);
  } else {
    catalogsOptions = [defaultSearchOption]
      .concat(customSearchOption ? [customSearchOption] : [])
      .concat(catalogsOptions);
  }

  return (
    <HeaderSearch
      selectedCatalog={selectedCatalog}
      catalogsOptions={catalogsOptions}
      className={className}
      isMobile={isMobile}
      onSelectCatalog={handleSelectCatalog}
    />
  );
};
